import React, { useState, useEffect } from 'react';
import { Plus, Building2, Users, CheckCircle, XCircle, Edit, Trash2, Key, Package, RefreshCw, AlertTriangle, Clock, Search, Filter, MoreVertical, MoreHorizontal, ArrowUpRight, Activity, Shield, LogOut, Check, X, Mail, ShieldAlert, Globe, Power } from 'lucide-react';
import axios from 'axios';
import ModuleAccessModal from '@/components/ModuleAccessModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { compressImage } from '@/utils/imageCompressor';

/**
 * Format an ISO timestamp as relative time for the "Last Active" column.
 * Returns { label, tier } where tier is 'fresh' | 'recent' | 'stale' | 'dormant' | 'never'.
 */
const formatLastActive = (iso) => {
  if (!iso) return { label: 'Never logged in', tier: 'never', exact: null };
  const then = new Date(iso);
  if (isNaN(then.getTime())) return { label: 'Never logged in', tier: 'never', exact: null };
  const now = new Date();
  const diffMs = now - then;
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  let label;
  if (sec < 60) label = 'Just now';
  else if (min < 60) label = `${min}m ago`;
  else if (hr < 24) label = `${hr}h ago`;
  else if (day < 30) label = `${day}d ago`;
  else if (day < 365) label = `${Math.floor(day / 30)}mo ago`;
  else label = `${Math.floor(day / 365)}y ago`;

  let tier = 'fresh';
  if (day >= 90) tier = 'dormant';
  else if (day >= 30) tier = 'stale';
  else if (day >= 7) tier = 'recent';

  return { label, tier, exact: then.toLocaleString() };
};

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showValidityModal, setShowValidityModal] = useState(false);
  const [validityInfo, setValidityInfo] = useState(null);
  const [validityTenant, setValidityTenant] = useState(null);
  const [validityForm, setValidityForm] = useState({ days_delta: '', end_date: '' });
  const [adjustingValidity, setAdjustingValidity] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedTenantForModules, setSelectedTenantForModules] = useState(null);
  const [selectedTenantForPassword, setSelectedTenantForPassword] = useState(null);
  const [selectedTenantForReset, setSelectedTenantForReset] = useState(null);
  const [resetOptions, setResetOptions] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [resetType, setResetType] = useState('selective'); // 'selective' or 'full'
  const [keepSettings, setKeepSettings] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    college_code: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    phone: '',
    address: '',
    logo: '',
    subscription_plan: '',
    billing_cycle: 'yearly',
    discount_amount: 0
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleOpenValidityModal = async (tenant) => {
    setValidityTenant(tenant);
    setValidityInfo(null);
    setValidityForm({ days_delta: '', end_date: '' });
    setShowValidityModal(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/subscriptions/validity/${tenant.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setValidityInfo(res.data);
    } catch (e) {
      setValidityInfo({ error: true });
    }
  };

  const handleAdjustValidity = async () => {
    const payload = {};
    if (validityForm.end_date) payload.end_date = validityForm.end_date;
    else if (validityForm.days_delta) payload.days_delta = parseInt(validityForm.days_delta, 10);
    if (!payload.end_date && !payload.days_delta) {
      alert('Enter days to add/remove OR pick an exact expiry date');
      return;
    }
    setAdjustingValidity(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/api/subscriptions/adjust-validity/${validityTenant.id}`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(`✅ ${res.data.message}\nNew expiry: ${res.data.new_end_date}\nDays remaining: ${res.data.days_remaining}`);
      setShowValidityModal(false);
      fetchTenants();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to adjust validity');
    } finally {
      setAdjustingValidity(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchSubscriptionPlans();
  }, []);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      alert('Failed to fetch tenants. You must be logged in as SuperAdmin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/subscription-plans/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubscriptionPlans(response.data);
      // Set default subscription plan to the first one if not already set
      if (response.data.length > 0) {
        setFormData(prev => ({
          ...prev,
          subscription_plan: prev.subscription_plan || response.data[0].code
        }));
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      // Set default plans if API fails
      const defaultPlans = [
        { id: '1', code: 'basic', name: 'Basic', max_configurable_modules: 0 },
        { id: '2', code: 'standard', name: 'Standard', max_configurable_modules: 5 },
        { id: '3', code: 'premium', name: 'Premium', max_configurable_modules: -1 }
      ];
      setSubscriptionPlans(defaultPlans);
      // Set default to first plan
      setFormData(prev => ({
        ...prev,
        subscription_plan: prev.subscription_plan || defaultPlans[0].code
      }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingTenant) {
        // Update existing tenant - only send fields the TenantUpdate model accepts
        const updateData = {
          name: formData.name || null,
          college_code: formData.college_code || null,
          phone: formData.phone || null,
          address: formData.address || null,
          logo: formData.logo || null,
          subscription_plan: formData.subscription_plan || null,
          billing_cycle: formData.billing_cycle || null,
          custom_renewal_price: formData.custom_renewal_price ? parseFloat(formData.custom_renewal_price) : null,
        };
        
        await axios.put(`${API_URL}/api/tenants/${editingTenant.id}`, updateData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        alert('Tenant updated successfully!');
      } else {
        // Create new tenant
        await axios.post(`${API_URL}/api/tenants/`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        alert('Tenant created successfully!');
      }
      
      setShowModal(false);
      setEditingTenant(null);
      setFormData({
        name: '',
        subdomain: '',
        college_code: '',
        admin_email: '',
        admin_name: '',
        admin_password: '',
        phone: '',
        address: '',
        logo: '',
        subscription_plan: '',
        billing_cycle: 'yearly',
        discount_amount: 0
      });
      fetchTenants();
    } catch (error) {
      const errDetail = error.response?.data?.detail;
      const errMsg = Array.isArray(errDetail)
        ? errDetail.map(e => e.msg || JSON.stringify(e)).join(', ')
        : (typeof errDetail === 'string' ? errDetail : `Failed to ${editingTenant ? 'update' : 'create'} tenant`);
      alert(errMsg);
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    // Get subscription data from tenant
    const subscription = tenant.subscription || {};
    setFormData({
      name: tenant.name,
      subdomain: tenant.subdomain,
      college_code: tenant.college_code || '',
      admin_email: tenant.admin_email,
      admin_name: tenant.admin_name,
      admin_password: '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      logo: tenant.logo || '',
      subscription_plan: tenant.subscription_plan || subscription.plan || 'basic',
      billing_cycle: subscription.plan_type || tenant.billing_cycle || 'yearly',
      custom_renewal_price: tenant.custom_renewal_price || ''
    });
    setShowModal(true);
  };

  const handleResetPassword = (tenant) => {
    setSelectedTenantForPassword(tenant);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const submitPasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/tenants/${selectedTenantForPassword.id}/reset-admin-password`,
        { new_password: newPassword },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert(`Password reset successfully for ${response.data.email}`);
      setShowPasswordModal(false);
      setSelectedTenantForPassword(null);
      setNewPassword('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleBlock = async (tenantId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this tenant?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/tenants/${tenantId}`,
        { is_active: !currentStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert(`Tenant ${action}d successfully!`);
      fetchTenants();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} tenant`);
    }
  };

  const handleDelete = async (tenantId, tenantName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${tenantName}"?\n\nThis will delete ALL data including students, staff, fees, and settings.\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    // Double confirm for safety
    const confirmText = window.prompt(`Type "${tenantName}" to confirm permanent deletion:`);
    if (confirmText !== tenantName) {
      alert('Deletion cancelled - tenant name did not match.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/tenants/${tenantId}/permanent`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Tenant permanently deleted!');
      fetchTenants();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  // Reset Data Functions
  const handleOpenResetModal = async (tenant) => {
    setSelectedTenantForReset(tenant);
    setResetLoading(true);
    setShowResetModal(true);
    setSelectedModules([]);
    setResetType('selective');
    setKeepSettings(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/${tenant.id}/reset-options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setResetOptions(response.data);
    } catch (error) {
      console.error('Error fetching reset options:', error);
      alert('Failed to fetch reset options');
      setShowResetModal(false);
    } finally {
      setResetLoading(false);
    }
  };

  const handleModuleSelect = (moduleKey) => {
    setSelectedModules(prev => 
      prev.includes(moduleKey) 
        ? prev.filter(m => m !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  const handleSelectAllModules = () => {
    if (resetOptions && selectedModules.length === resetOptions.modules.length) {
      setSelectedModules([]);
    } else if (resetOptions) {
      setSelectedModules(resetOptions.modules.map(m => m.key));
    }
  };

  const handleResetData = async () => {
    const confirmMsg = resetType === 'full' 
      ? `Are you ABSOLUTELY sure you want to FULL RESET all data for "${selectedTenantForReset?.name}"? This will delete ALL records!`
      : `Are you sure you want to reset ${selectedModules.length} module(s) for "${selectedTenantForReset?.name}"?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    // Double confirmation for full reset
    if (resetType === 'full') {
      const typedConfirm = window.prompt('Type "FULL RESET" to confirm:');
      if (typedConfirm !== 'FULL RESET') {
        alert('Reset cancelled. You must type "FULL RESET" exactly.');
        return;
      }
    }
    
    setResetLoading(true);
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (resetType === 'full') {
        response = await axios.post(
          `${API_URL}/api/tenants/${selectedTenantForReset.id}/full-reset`,
          { confirm: true, keep_settings: keepSettings },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/api/tenants/${selectedTenantForReset.id}/reset-module`,
          { modules: selectedModules },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      alert(`Reset successful! Deleted ${response.data.total_deleted} records.`);
      setShowResetModal(false);
      setSelectedTenantForReset(null);
      setResetOptions(null);
      // Refresh tenant list so Quick View counts (students, staff, etc.) update immediately
      fetchTenants();
    } catch (error) {
      console.error('Error resetting data:', error);
      alert(error.response?.data?.detail || 'Failed to reset data');
    } finally {
      setResetLoading(false);
    }
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800'
    };
    return colors[plan] || colors.free;
  };

  const filteredTenants = tenants.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          t.subdomain.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (t.admin_name && t.admin_name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' ? true : 
                          statusFilter === 'active' ? t.is_active : !t.is_active;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">Loading tenants...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8 font-sans">
      {/* Header Area */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Tenant Management</h1>
          <p className="text-slate-500 mt-1 font-medium">Command center for all platform organizations</p>
        </div>
        <button
          onClick={() => {
            setEditingTenant(null);
            setFormData({
              name: '', subdomain: '', college_code: '', admin_email: '', admin_name: '', admin_password: '',
              phone: '', address: '', logo: '', subscription_plan: subscriptionPlans.length > 0 ? subscriptionPlans[0].code : '',
              billing_cycle: 'yearly', discount_amount: 0
            });
            setShowModal(true);
          }}
          className="group flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-all shadow-sm font-semibold text-sm"
          data-testid="create-tenant-btn"
        >
          <Plus className="h-4 w-4 transition-transform group-hover:rotate-90" />
          Create Tenant
        </button>
      </div>

      {/* KPI Stats Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:border-blue-200 transition-colors">
          <div className="absolute -right-6 -top-6 text-slate-50 opacity-50 group-hover:scale-110 transition-transform duration-500">
            <Building2 className="w-32 h-32" />
          </div>
          <div className="relative z-10">
            <p className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1">Total Tenants</p>
            <p className="text-4xl font-extrabold text-slate-900">{tenants.length}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:border-emerald-200 transition-colors">
          <div className="absolute -right-6 -top-6 text-emerald-50 opacity-50 group-hover:scale-110 transition-transform duration-500">
            <Activity className="w-32 h-32" />
          </div>
          <div className="relative z-10 flex justify-between items-end">
            <div>
              <p className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1">Active</p>
              <p className="text-4xl font-extrabold text-emerald-600">{tenants.filter(t => t.is_active).length}</p>
            </div>
            <div className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1 mb-1">
              <ArrowUpRight className="w-3 h-3" /> Healthy
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:border-rose-200 transition-colors">
          <div className="absolute -right-6 -top-6 text-rose-50 opacity-50 group-hover:scale-110 transition-transform duration-500">
            <ShieldAlert className="w-32 h-32" />
          </div>
          <div className="relative z-10">
            <p className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1">Inactive</p>
            <p className="text-4xl font-extrabold text-rose-600">{tenants.filter(t => !t.is_active).length}</p>
          </div>
        </div>
      </div>

      {/* Toolbar: Search & Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, subdomain, or admin..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-slate-900"
            data-testid="tenant-search-input"
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 pl-10 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white appearance-none font-medium text-slate-700"
              data-testid="tenant-status-filter"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {filteredTenants.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center justify-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 text-slate-400">
              <Search className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">No tenants found</h3>
            <p className="text-slate-500 text-sm mt-1 max-w-sm">We couldn't find any organizations matching your current search or filter criteria.</p>
            {(searchQuery || statusFilter !== 'all') && (
              <button onClick={() => { setSearchQuery(''); setStatusFilter('all'); }} className="mt-4 text-blue-600 text-sm font-semibold hover:underline">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50/80 text-xs text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Organization</th>
                  <th className="px-6 py-4">Identifiers</th>
                  <th className="px-6 py-4">Admin</th>
                  <th className="px-6 py-4">Plan & Status</th>
                  <th className="px-6 py-4">Activity</th>
                  <th className="px-6 py-4 text-right sticky right-0 bg-slate-50/80 backdrop-blur-sm shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.02)]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-slate-50/80 transition-colors group">
                    {/* Organization Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {tenant.logo ? (
                          <div className="w-10 h-10 rounded-lg border border-slate-200 bg-white p-1 flex-shrink-0">
                            <img src={tenant.logo} alt={tenant.name} className="w-full h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0 text-slate-400">
                            <Building2 className="w-5 h-5" />
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-slate-900 leading-tight">{tenant.name}</p>
                          <div className="flex items-center gap-3 mt-1 text-slate-500 text-xs font-medium">
                            {tenant.phone && <span>{tenant.phone}</span>}
                            {tenant.address && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                <span className="truncate max-w-[120px]" title={tenant.address}>{tenant.address}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Identifiers (Group, Subdomain, College Code) */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-slate-400" />
                          <code className="text-xs font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">/{tenant.subdomain}</code>
                        </div>
                        {tenant.college_code ? (
                          <div className="flex items-center gap-2">
                            <Shield className="w-3.5 h-3.5 text-blue-400" />
                            <code className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">{tenant.college_code}</code>
                          </div>
                        ) : null}
                        {tenant.group_id && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <Users className="w-3.5 h-3.5 text-purple-400" />
                            <span className="text-[10px] font-bold uppercase tracking-wide text-purple-700">{tenant.group_name || 'Grouped'}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Admin Column */}
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{tenant.admin_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 text-slate-500">
                        <Mail className="w-3 h-3" />
                        <span className="text-xs">{tenant.admin_email}</span>
                      </div>
                    </td>

                    {/* Plan & Status */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2 items-start">
                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase border ${
                          tenant.subscription_plan === 'premium' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                          tenant.subscription_plan === 'standard' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                          'bg-slate-100 text-slate-700 border-slate-200'
                        }`}>
                          {tenant.subscription_plan || 'Free'}
                        </span>
                        
                        <div className="flex items-center gap-1.5">
                          <span className={`relative flex h-2 w-2`}>
                            {tenant.is_active && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>}
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${tenant.is_active ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                          </span>
                          <span className={`text-xs font-semibold ${tenant.is_active ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {tenant.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Activity */}
                    <td className="px-6 py-4" data-testid={`tenant-last-active-${tenant.id}`}>
                      {(() => {
                        const { label, tier, exact } = formatLastActive(tenant.last_activity_at);
                        const isNever = tier === 'never';
                        return (
                          <div title={exact || 'No activity'} className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                              <Clock className={`w-3.5 h-3.5 ${isNever ? 'text-slate-400' : 'text-slate-600'}`} />
                              <span className={`text-xs font-semibold ${
                                tier === 'fresh' ? 'text-emerald-600' :
                                tier === 'recent' ? 'text-blue-600' :
                                tier === 'stale' ? 'text-amber-600' :
                                tier === 'dormant' ? 'text-rose-600' : 'text-slate-500'
                              }`}>{label}</span>
                            </div>
                            {tenant.last_activity_role && !isNever && (
                              <span className="text-[10px] text-slate-400 font-medium ml-5 mt-0.5">{tenant.last_activity_role}</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    {/* Actions Dropdown */}
                    <td className="px-6 py-4 text-right sticky right-0 bg-white group-hover:bg-slate-50/80 transition-colors shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.02)]">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 border-slate-200 hover:border-slate-300 hover:bg-slate-100 data-[state=open]:bg-slate-100">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4 text-slate-600" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 font-medium">
                          <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wider">Management</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleEdit(tenant)} className="cursor-pointer">
                            <Edit className="mr-2 h-4 w-4 text-blue-600" /> Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedTenantForModules(tenant); setShowModuleModal(true); }} className="cursor-pointer">
                            <Package className="mr-2 h-4 w-4 text-indigo-600" /> Module Access
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenValidityModal(tenant)} className="cursor-pointer" data-testid={`adjust-validity-${tenant.id}`}>
                            <Clock className="mr-2 h-4 w-4 text-teal-600" /> Adjust Validity
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResetPassword(tenant)} className="cursor-pointer">
                            <Key className="mr-2 h-4 w-4 text-purple-600" /> Reset Password
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator />
                          
                          <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wider">Danger Zone</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleBlock(tenant.id, tenant.is_active)} className="cursor-pointer">
                            {tenant.is_active ? (
                              <><Power className="mr-2 h-4 w-4 text-amber-600" /> Deactivate Tenant</>
                            ) : (
                              <><Check className="mr-2 h-4 w-4 text-emerald-600" /> Activate Tenant</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenResetModal(tenant)} className="cursor-pointer">
                            <RefreshCw className="mr-2 h-4 w-4 text-orange-600" /> Reset Data
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(tenant.id, tenant.name)} className="cursor-pointer text-rose-600 focus:text-rose-700 focus:bg-rose-50">
                            <Trash2 className="mr-2 h-4 w-4" /> Permanent Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adjust Validity Modal */}
      {showValidityModal && validityTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="validity-modal">
            <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
              <Clock className="h-5 w-5 text-teal-600" /> Adjust Validity
            </h2>
            <p className="text-sm text-gray-500 mb-4">{validityTenant.name}</p>

            {validityInfo === null ? (
              <p className="text-sm text-gray-400 mb-4">Loading current validity...</p>
            ) : validityInfo.error ? (
              <p className="text-sm text-red-500 mb-4">Could not load current validity</p>
            ) : (
              <div className="bg-slate-50 rounded-lg p-4 mb-4 flex justify-between items-center">
                <div>
                  <p className="text-xs text-gray-500">Current Expiry</p>
                  <p className="font-bold" data-testid="validity-current-end">{validityInfo.end_date || 'Not set'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Days Remaining</p>
                  <p className={`font-bold text-lg ${(validityInfo.days_remaining ?? 0) > 30 ? 'text-green-600' : (validityInfo.days_remaining ?? 0) > 7 ? 'text-amber-600' : 'text-red-600'}`} data-testid="validity-days-remaining">
                    {validityInfo.days_remaining ?? '—'}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Add / Remove Days</label>
                <input
                  type="number"
                  data-testid="validity-days-delta-input"
                  value={validityForm.days_delta}
                  onChange={(e) => setValidityForm({ days_delta: e.target.value, end_date: '' })}
                  placeholder="e.g. 30 (add) or -30 (reduce)"
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-400 mt-1">Negative number diley days kombe (e.g. -30 = 30 din kom)</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400"><span className="flex-1 border-t"></span>OR<span className="flex-1 border-t"></span></div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Set Exact Expiry Date</label>
                <input
                  type="date"
                  data-testid="validity-end-date-input"
                  value={validityForm.end_date}
                  onChange={(e) => setValidityForm({ end_date: e.target.value, days_delta: '' })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                data-testid="validity-apply-btn"
                onClick={handleAdjustValidity}
                disabled={adjustingValidity}
                className="flex-1 bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 font-semibold"
              >
                {adjustingValidity ? 'Applying...' : 'Apply Change'}
              </button>
              <button onClick={() => setShowValidityModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Tenant Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8">
            <h2 className="text-2xl font-bold mb-6">
              {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
            </h2>
            <form onSubmit={handleCreate}>
              <div className="max-h-[70vh] overflow-y-auto pr-2">
                {/* Organization Details Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b">Organization Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="ABC School"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tenant Code (URL Path) *
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 whitespace-nowrap">cloudcampus.site/</span>
                        <input
                          type="text"
                          required
                          value={formData.subdomain}
                          onChange={(e) => setFormData({...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="dcp"
                          readOnly={editingTenant}
                          disabled={editingTenant}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Only lowercase letters and numbers allowed</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        College Code * <span className="text-xs text-blue-600">(Used in Student ID)</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.college_code}
                        onChange={(e) => setFormData({...formData, college_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
                        placeholder="ILOE"
                        maxLength="10"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Student ID: <code className="bg-gray-100 px-1 rounded">{formData.college_code || 'CODE'}ED2025001</code>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="1234567890"
                        maxLength="15"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Full address"
                        rows="2"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization Logo
                      </label>
                      <div className="flex items-start gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                alert('File size must be less than 5MB');
                                return;
                              }
                              try {
                                const result = await compressImage(file, { maxDim: 400, quality: 0.85 });
                                setFormData({ ...formData, logo: result.dataUrl });
                                console.log(`Logo compressed: ${result.width}x${result.height}, ~${result.sizeKB}KB`);
                              } catch (err) {
                                alert('Failed to process logo: ' + err.message);
                              }
                            }
                          }}
                          className="flex-1 px-3 py-2 border rounded-lg"
                        />
                        {formData.logo && (
                          <img 
                            src={formData.logo} 
                            alt="Logo preview" 
                            className="h-16 w-16 object-contain border rounded bg-gray-50"
                          />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG (auto-compressed to 400px, ~50KB)</p>
                    </div>
                  </div>
                </div>

                {/* Admin Details Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b">Admin User Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.admin_name}
                        onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.admin_email}
                        onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="admin@example.com"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Admin Password {!editingTenant && '*'}
                      </label>
                      <input
                        type="password"
                        required={!editingTenant}
                        value={formData.admin_password}
                        onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder={editingTenant ? "Leave blank to keep current password" : "Min 8 characters"}
                        minLength="8"
                      />
                      {editingTenant && (
                        <p className="text-xs text-gray-500 mt-1">Leave blank to keep the current password</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Subscription Section */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 pb-2 border-b">Subscription Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Plan *
                      </label>
                      <select
                        value={formData.subscription_plan}
                        onChange={(e) => {
                          setFormData({...formData, subscription_plan: e.target.value});
                        }}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        {subscriptionPlans.length === 0 ? (
                          <>
                            <option value="basic">Basic</option>
                            <option value="standard">Standard</option>
                            <option value="premium">Premium</option>
                          </>
                        ) : (
                          subscriptionPlans.map(plan => (
                            <option key={plan.id || plan.code} value={plan.code}>
                              {plan.name} 
                              {plan.max_configurable_modules !== undefined && 
                                ` (${plan.max_configurable_modules === -1 ? 'Unlimited' : plan.max_configurable_modules} modules)`
                              }
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Billing Cycle *
                      </label>
                      <div className="flex gap-3">
                        <label className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all border-blue-500 bg-blue-50`}>
                          <input
                            type="radio"
                            name="billing_cycle"
                            value="yearly"
                            checked={true}
                            readOnly
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="font-medium text-gray-800">Yearly</span>
                            {(() => {
                              const plan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                              if (plan?.price_yearly > 0) {
                                const gst = plan.gst_percent || 0;
                                const gstIncl = plan.gst_included || false;
                                let total = plan.price_yearly;
                                if (gst > 0 && !gstIncl) {
                                  total = Math.round(plan.price_yearly * (1 + gst/100));
                                }
                                return (
                                  <div>
                                    <p className="text-sm text-blue-600 font-bold">₹{total.toLocaleString('en-IN')}/yr</p>
                                    {gst > 0 && <p className="text-xs text-gray-500">{gstIncl ? `Incl.` : `+`} {gst}% GST</p>}
                                  </div>
                                );
                              }
                              return <p className="text-sm text-green-600">Free</p>;
                            })()}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Discount Field - only for new tenants */}
                  {!editingTenant && formData.subscription_plan && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount on First Time Purchase (₹)
                      </label>
                      <input
                        type="number"
                        value={formData.discount_amount}
                        onChange={(e) => setFormData({...formData, discount_amount: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                        placeholder="Enter discount amount"
                      />
                    </div>
                  )}

                  {/* Custom Renewal Price - for both new and editing tenants */}
                  {formData.subscription_plan && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Renewal Price (₹)
                      </label>
                      <input
                        type="number"
                        value={formData.custom_renewal_price}
                        onChange={(e) => setFormData({...formData, custom_renewal_price: parseInt(e.target.value) || ''})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                        placeholder="Leave empty to use plan's default renewal price"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Set a specific renewal price for this tenant. If empty, the plan's default renewal price will be used.
                      </p>
                    </div>
                  )}

                  {/* Selected Plan Summary */}
                  {formData.subscription_plan && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      {(() => {
                        const selectedPlan = subscriptionPlans.find(p => p.code === formData.subscription_plan);
                        if (!selectedPlan) return null;
                        
                        const basePrice = selectedPlan.price_yearly || 0;
                        const cycle = 'year';
                        const discountAmt = formData.discount_amount || 0;
                        const priceAfterDiscount = Math.max(0, basePrice - discountAmt);
                        
                        // GST Calculation on discounted price
                        const gstPercent = selectedPlan.gst_percent || 0;
                        const gstIncluded = selectedPlan.gst_included || false;
                        let gstAmount = 0;
                        let totalPrice = priceAfterDiscount;
                        
                        if (gstPercent > 0 && priceAfterDiscount > 0) {
                          if (gstIncluded) {
                            gstAmount = Math.round(priceAfterDiscount - (priceAfterDiscount / (1 + gstPercent/100)));
                          } else {
                            gstAmount = Math.round(priceAfterDiscount * gstPercent / 100);
                            totalPrice = priceAfterDiscount + gstAmount;
                          }
                        }
                        
                        return (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-800">
                                {selectedPlan.name} Plan - {formData.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {selectedPlan.max_students === -1 ? 'Unlimited' : selectedPlan.max_students} Students • 
                                {selectedPlan.max_staff === -1 ? ' Unlimited' : ` ${selectedPlan.max_staff}`} Staff • 
                                {selectedPlan.max_configurable_modules === -1 
                                  ? ' Unlimited Modules' 
                                  : selectedPlan.max_configurable_modules === 0 
                                    ? ' Fixed Modules Only'
                                    : ` ${selectedPlan.max_configurable_modules} Configurable Modules`
                                }
                              </p>
                            </div>
                            <div className="text-right">
                              {basePrice > 0 ? (
                                <>
                                  <p className="text-lg text-gray-600">
                                    ₹{basePrice.toLocaleString('en-IN')}
                                    {gstPercent > 0 && !gstIncluded && (
                                      <span className="text-sm"> + GST</span>
                                    )}
                                  </p>
                                  {discountAmt > 0 && (
                                    <p className="text-sm text-red-500">
                                      Discount: -₹{discountAmt.toLocaleString('en-IN')}
                                    </p>
                                  )}
                                  {gstPercent > 0 && (
                                    <p className="text-xs text-gray-500">
                                      GST ({gstPercent}%): ₹{gstAmount.toLocaleString('en-IN')}
                                    </p>
                                  )}
                                  <p className="text-2xl font-bold text-blue-600">
                                    ₹{totalPrice.toLocaleString('en-IN')}
                                  </p>
                                  <p className="text-sm text-gray-500">Total per {cycle}</p>
                                </>
                              ) : (
                                <p className="text-2xl font-bold text-green-600">Free</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons - Fixed at bottom */}
              <div className="flex gap-3 mt-6 pt-4 border-t bg-white sticky bottom-0">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  {editingTenant ? 'Update Tenant' : 'Create Tenant'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTenant(null);
                    setFormData({
                      name: '',
                      subdomain: '',
                      admin_email: '',
                      admin_name: '',
                      admin_password: '',
                      phone: '',
                      address: '',
                      logo: '',
                      subscription_plan: ''
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedTenantForPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Key className="h-5 w-5 text-purple-600" />
              Reset Admin Password
            </h2>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Tenant:</strong> {selectedTenantForPassword.name}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Admin Email:</strong> {selectedTenantForPassword.admin_email}
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password *
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Enter new password (min 6 characters)"
                minLength="6"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={submitPasswordReset}
                className="flex-1 bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 font-medium transition-colors"
              >
                Reset Password
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedTenantForPassword(null);
                  setNewPassword('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Module Access Modal */}
      <ModuleAccessModal
        isOpen={showModuleModal}
        onClose={() => {
          setShowModuleModal(false);
          setSelectedTenantForModules(null);
        }}
        tenant={selectedTenantForModules}
        onUpdate={fetchTenants}
      />

      {/* Reset Data Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Reset Tenant Data - {selectedTenantForReset?.name}
            </DialogTitle>
          </DialogHeader>

          {resetLoading && !resetOptions ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-2" />
              <p className="text-gray-600">Loading reset options...</p>
            </div>
          ) : resetOptions ? (
            <div className="space-y-4">
              {/* Warning Banner */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Warning: This action cannot be undone!
                </p>
                <p className="text-red-600 text-sm mt-1">
                  Total Records: <strong>{resetOptions.total_records.toLocaleString('en-IN')}</strong>
                </p>
              </div>

              {/* Reset Type Selection */}
              <div className="space-y-3">
                <label className="font-medium text-gray-700">Reset Type:</label>
                <div className="flex gap-4">
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    resetType === 'selective' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="resetType"
                      value="selective"
                      checked={resetType === 'selective'}
                      onChange={() => setResetType('selective')}
                      className="mr-2"
                    />
                    <span className="font-medium">Selective Reset</span>
                    <p className="text-sm text-gray-500 mt-1">Choose specific modules to reset</p>
                  </label>
                  <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    resetType === 'full' ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="resetType"
                      value="full"
                      checked={resetType === 'full'}
                      onChange={() => setResetType('full')}
                      className="mr-2"
                    />
                    <span className="font-medium text-red-600">Full Reset</span>
                    <p className="text-sm text-gray-500 mt-1">Delete ALL tenant data</p>
                  </label>
                </div>
              </div>

              {/* Selective Reset - Module Selection */}
              {resetType === 'selective' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="font-medium text-gray-700">Select Modules to Reset:</label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllModules}
                    >
                      {selectedModules.length === resetOptions.modules.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                    {resetOptions.modules.map((module) => (
                      <label
                        key={module.key}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedModules.includes(module.key)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <Checkbox
                          checked={selectedModules.includes(module.key)}
                          onCheckedChange={() => handleModuleSelect(module.key)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{module.name}</p>
                          <p className="text-xs text-gray-500">{module.description}</p>
                          <p className={`text-xs font-medium mt-1 ${
                            module.record_count > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {module.record_count.toLocaleString('en-IN')} records
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Reset - Settings Option */}
              {resetType === 'full' && (
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer bg-yellow-50 border-yellow-200">
                    <Checkbox
                      checked={keepSettings}
                      onCheckedChange={setKeepSettings}
                    />
                    <div>
                      <p className="font-medium text-gray-800">Keep Master Settings</p>
                      <p className="text-sm text-gray-500">
                        {keepSettings
                          ? 'Checked: Preserves courses, semesters, subjects, sessions, classrooms, departments, fee structures, timezone/currency settings, etc.'
                          : 'Unchecked: WILL DELETE courses, semesters, subjects, sessions, classrooms, departments, fee structures, timezone/currency/college/profile settings — everything becomes blank.'}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-700">Summary:</p>
                {resetType === 'selective' ? (
                  <p className="text-sm text-gray-600">
                    {selectedModules.length} module(s) selected: {' '}
                    {selectedModules.length > 0 
                      ? resetOptions.modules
                          .filter(m => selectedModules.includes(m.key))
                          .reduce((sum, m) => sum + m.record_count, 0)
                          .toLocaleString('en-IN') + ' records will be deleted'
                      : 'No modules selected'}
                  </p>
                ) : (
                  <p className="text-sm text-red-600 font-medium">
                    ALL {resetOptions.total_records.toLocaleString('en-IN')} records will be deleted
                    {keepSettings ? ' (settings preserved)' : ' (including all settings)'}
                  </p>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowResetModal(false);
                setSelectedTenantForReset(null);
                setResetOptions(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetData}
              disabled={resetLoading || (resetType === 'selective' && selectedModules.length === 0)}
            >
              {resetLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {resetType === 'full' ? 'Full Reset' : 'Reset Selected'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantManagement;
